const STORED_PACKAGE_ROUTE_SEGMENT_REGEX =
  /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;

export const NEW_PACKAGE_INPUT_REGEX = STORED_PACKAGE_ROUTE_SEGMENT_REGEX;

export type DappRouteMode = 'legacy-id' | 'package';

export interface DappRouteDapp {
  id: string;
  packageName?: string | null;
}

function isPackageRouteSegment(value: string): boolean {
  return STORED_PACKAGE_ROUTE_SEGMENT_REGEX.test(value);
}

export function decodeDappRouteSegment(value: string): string | null {
  if (value.length === 0 || value !== value.trim() || /[\s/?#%]/.test(value)) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(value);
    if (
      decoded.length === 0 ||
      decoded !== decoded.trim() ||
      /[\s/?#%]/.test(decoded)
    ) {
      return null;
    }

    return decoded;
  } catch {
    return null;
  }
}

export function getDappRouteSegment(
  dapp: DappRouteDapp,
  options: { mode?: DappRouteMode } = {},
): string {
  if (options.mode === 'legacy-id') {
    return dapp.id;
  }

  if (dapp.packageName && isPackageRouteSegment(dapp.packageName)) {
    return dapp.packageName;
  }

  return dapp.id;
}

export function getDappRouteSegmentFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/dapp\/([^/]+)/);
  if (!match) {
    return null;
  }

  return decodeDappRouteSegment(match[1]);
}

export function getDappPathBySegment(segment: string, childPath = ''): string {
  const normalizedChildPath =
    childPath.length === 0
      ? ''
      : childPath.startsWith('/')
        ? childPath
        : `/${childPath}`;

  return `/dapp/${encodeURIComponent(segment)}${normalizedChildPath}`;
}

export function getDappHomePath(
  dapp: DappRouteDapp,
  options: { mode?: DappRouteMode } = {},
): string {
  return getDappPathBySegment(getDappRouteSegment(dapp, options));
}

export function getDappDetailsPath(
  dapp: DappRouteDapp,
  options: { mode?: DappRouteMode } = {},
): string {
  return getDappPathBySegment(getDappRouteSegment(dapp, options), '/details');
}

export function getDappReviewsPath(
  dapp: DappRouteDapp,
  options: { mode?: DappRouteMode } = {},
): string {
  return getDappPathBySegment(getDappRouteSegment(dapp, options), '/reviews');
}

export function getDappQuestsPath(
  dapp: DappRouteDapp,
  options: { mode?: DappRouteMode } = {},
): string {
  return getDappPathBySegment(getDappRouteSegment(dapp, options), '/quests');
}

export function getDappNewVersionPath(
  dapp: DappRouteDapp,
  options: { mode?: DappRouteMode } = {},
): string {
  return getDappPathBySegment(
    getDappRouteSegment(dapp, options),
    '/new-version',
  );
}

export function getDappReleasePath(
  dapp: DappRouteDapp,
  releaseId: string,
  options: { mode?: DappRouteMode } = {},
): string {
  return getDappPathBySegment(
    getDappRouteSegment(dapp, options),
    `/release/${releaseId}`,
  );
}

export function getAdminDappPath(
  dapp: DappRouteDapp,
  options: { mode?: DappRouteMode } = {},
): string {
  return `/dapps/${encodeURIComponent(getDappRouteSegment(dapp, options))}`;
}

export function resolveVisibleDappFromRouteSegment<T extends DappRouteDapp>(
  segment: string | null,
  dapps: T[],
): T | null {
  if (!segment) {
    return null;
  }

  const exactIdMatch = dapps.find((dapp) => dapp.id === segment);
  if (exactIdMatch) {
    return exactIdMatch;
  }

  const exactPackageMatch = dapps.find((dapp) => dapp.packageName === segment);
  if (exactPackageMatch) {
    return exactPackageMatch;
  }

  const normalizedSegment = segment.toLowerCase();
  const packageMatches = dapps.filter(
    (dapp) => dapp.packageName?.toLowerCase() === normalizedSegment,
  );

  return packageMatches.length === 1 ? packageMatches[0] : null;
}

export function replaceDappRouteSegment(
  pathname: string,
  canonicalSegment: string,
): string {
  const match = pathname.match(/^\/dapp\/([^/]+)(\/.*)?$/);
  const childPath = match?.[2] ?? '';
  return getDappPathBySegment(canonicalSegment, childPath);
}

export function replaceAdminDappRouteSegment(
  pathname: string,
  canonicalSegment: string,
): string {
  if (!pathname.startsWith('/dapps/')) {
    return `/dapps/${encodeURIComponent(canonicalSegment)}`;
  }

  return `/dapps/${encodeURIComponent(canonicalSegment)}`;
}
